let scrollPosition = 0;

// ─── Concurrency guards ───────────────────────────────────────────────────────
let _cartUpdating    = false;
let _cartVersion     = 0;        // incremented on every committed change
let _cartFetchPromise = null;    // deduplicates /cart.js fetches

// ─── Animation constants ─────────────────────────────────────────────────────
const FADE_OUT_MS  = 130;   // content fade-out before swap
const ITEM_STEP_MS = 55;    // stagger between successive items
const ITEM_DUR_MS  = 260;   // each item's fade-up duration

// ─── Modal open / close ──────────────────────────────────────────────────────

function openCartModal() {
  const cartModal = document.getElementById('cart-modal');
  if (!cartModal) return;
  scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
  document.body.style.top      = `-${scrollPosition}px`;
  document.body.style.position = 'fixed';
  document.body.style.width    = '100%';
  document.body.style.overflow = 'hidden';
  cartModal.classList.add('active');
  cartModal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('cart-open', 'modal-open');
}

function closeCartModal(skipScrollRestore) {
  const cartModal = document.getElementById('cart-modal');
  if (!cartModal) return;
  cartModal.classList.remove('active');
  cartModal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('cart-open', 'modal-open');
  document.body.style.position = '';
  document.body.style.top      = '';
  document.body.style.width    = '';
  document.body.style.overflow = '';
  if (!skipScrollRestore) window.scrollTo(0, scrollPosition);
}

function isCartOpen() {
  return document.getElementById('cart-modal')?.classList.contains('active') ?? false;
}

// ─── Cart count badge ────────────────────────────────────────────────────────

function updateCartCount(count) {
  document.querySelectorAll('.cart-count').forEach(el => {
    if (count > 0) {
      el.textContent = count;
      el.style.display = '';
    } else {
      el.textContent = el.classList.contains('cart-count--mobile') ? '' : '0';
    }
  });
  document.querySelectorAll('.cart-text .cart-count').forEach(el => {
    el.textContent = count;
  });
}

// ─── Qty button visual state ─────────────────────────────────────────────────

function updateQtyButtonsState() {
  document.querySelectorAll('.cart-item').forEach(item => {
    const controls = item.querySelector('.cart-item__qty-controls');
    if (!controls) return;
    const qty  = Number(controls.dataset.current);
    const max  = Number(controls.dataset.max) || 999;
    const plus  = controls.querySelector('.qty-increase');
    const minus = controls.querySelector('.qty-decrease');
    if (plus)  plus.classList.toggle('disabled', qty >= max);
    if (minus) minus.classList.toggle('disabled', qty <= 0);
  });
}

// ─── Inventory over-cap correction ──────────────────────────────────────────

async function fixCartQuantities(cart) {
  for (const [index, item] of cart.items.entries()) {
    const max = window.variantInventory?.[item.variant_id] ?? 999;
    if (item.quantity > max) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: index + 1, quantity: max }),
      });
      return true;
    }
  }
  return false;
}

// ─── HTML builder ─────────────────────────────────────────────────────────────
// Items are displayed newest-first (Shopify stores oldest-first; we reverse
// for display while keeping the correct 1-based line indices for API calls).

const _ARROW = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 5C16 5.742 16.733 6.85 17.475 7.78C18.429 8.98 19.569 10.027 20.876 10.826C21.856 11.425 23.044 12 24 12M24 12C23.044 12 21.855 12.575 20.876 13.174C19.569 13.974 18.429 15.021 17.475 16.219C16.733 17.15 16 18.26 16 19M24 12H0" stroke="currentColor"/></svg>`;
const _CONTINUE = `<button type="button" class="continue-shopping" style="all:unset;cursor:pointer;display:flex;align-items:center;gap:0.5rem;color:var(--dark-grey);"><span>Continue Shopping</span>${_ARROW}</button>`;

function buildCartHTML(cart) {
  const taxRate = 0.10;

  if (cart.items.length === 0) {
    return `
      <div class="cart-container">
        <div class="cart-header">
          <p class="cart-title">Cart (0)</p>${_CONTINUE}
        </div>
        <div class="cart-divider"></div>
        <div class="cart-items">
          <div class="cart-empty">
            <p>Your cart is empty.</p>
            <a href="/collections/all" class="checkout-button">Browse Products</a>
          </div>
        </div>
      </div>`;
  }

  const totalWithTax = Math.round(cart.total_price * (1 + taxRate));

  // Reverse for newest-first display; preserve original index for line-index
  const displayItems = cart.items
    .map((item, i) => ({ item, lineIndex: i + 1 }))
    .reverse();

  const itemsHTML = displayItems.map(({ item, lineIndex }) => {
    const qty        = item.quantity;
    const variantId  = item.variant_id;
    const max        = window.variantInventory?.[variantId] ?? 999;
    const unitPrice  = Math.round(item.final_price * (1 + taxRate));
    const optionInfo = (item.options_with_values || [])
      .map(o => `<div class="option-row"><span class="option-label">${o.name}:</span><span class="option-value">${o.value}</span></div>`)
      .join('');
    return `
      <div class="cart-item grid" data-line-index="${lineIndex}">
        <div class="cart-item__image col-span-3">
          <img src="${item.image}" alt="${item.product_title}" loading="lazy">
        </div>
        <div class="cart-item__info col-span-5">
          <div class="cart-item__top-row">
            <p class="cart-item__title">${item.product_title}</p>
            <div class="cart-item__qty-controls"
                 data-line-index="${lineIndex}" data-variant-id="${variantId}"
                 data-max="${max}" data-current="${qty}">
              <span class="qty-btn qty-decrease${qty <= 0 ? ' disabled' : ''}" role="button" tabindex="0">−</span>
              <span class="qty-display">${qty}</span>
              <span class="qty-btn qty-increase${qty >= max ? ' disabled' : ''}" role="button" tabindex="0">＋</span>
            </div>
          </div>
          <p class="cart-item__price">${Shopify.formatMoney(unitPrice)} <span class="tax-note">(tax in)</span></p>
          <div class="cart-item__detailsandremove__container">
            <div class="cart-item__details">${optionInfo}</div>
            <div class="cart-item__remove-row">
              <span class="cart-item__remove" data-remove-index="${lineIndex}" role="button" tabindex="0">Remove</span>
            </div>
          </div>
        </div>
      </div>`;
  }).join('');

  return `
    <div class="cart-container">
      <div class="cart-header">
        <p class="cart-title">Cart (${cart.item_count})</p>${_CONTINUE}
      </div>
      <div class="cart-divider"></div>
      <div class="cart-items">${itemsHTML}</div>
      <div class="cart-divider"></div>
      <div class="cart-footer">
        <div class="cart-total-row">
          <span class="cart-total-label">Total</span>
          <span class="cart-total-amount">${Shopify.formatMoney(totalWithTax)}</span>
        </div>
        <p class="cart-tax-note">Price includes tax.</p>
        <a href="/checkout" class="checkout-button">Checkout</a>
        <p class="cart-shipping-note">Shipping calculated at checkout.</p>
      </div>
    </div>`;
}

// ─── Sync render (no animation) ──────────────────────────────────────────────
// Used for background prefetches (cart is closed) and cache hits.

function renderCartData(cart, cartBody) {
  if (!cartBody || !cart) return;
  cartBody.innerHTML = buildCartHTML(cart);
  updateQtyButtonsState();
}

// ─── Animated render ──────────────────────────────────────────────────────────
// • Fades out any existing visible content (130 ms).
// • Swaps to new HTML.
// • Staggers the cart items + footer in from top-to-bottom (55 ms apart).
// • Only plays animations when the cart panel is actually open — background
//   prefetches skip straight to the sync render.

async function animatedRenderCart(cartBody, cart) {
  if (!cartBody || !cart) return;

  const open       = isCartOpen();
  const hasContent = open && !!cartBody.querySelector('.cart-item, .cart-empty');

  // 1 — Fade out existing content
  if (hasContent) {
    const container = cartBody.querySelector('.cart-container');
    if (container) {
      container.style.transition = `opacity ${FADE_OUT_MS}ms ease`;
      container.style.opacity    = '0';
      await new Promise(r => setTimeout(r, FADE_OUT_MS + 20));
    }
  }

  // 2 — Swap HTML
  cartBody.innerHTML = buildCartHTML(cart);
  updateQtyButtonsState();

  if (!open) return; // background — no need to animate

  // 3 — Stagger items + footer in
  const targets = [
    ...cartBody.querySelectorAll('.cart-item, .cart-empty'),
    cartBody.querySelector('.cart-footer'),
  ].filter(Boolean);

  targets.forEach((el, i) => {
    el.style.animationDelay    = `${i * ITEM_STEP_MS}ms`;
    el.style.animationDuration = `${ITEM_DUR_MS}ms`;
    el.classList.add('cart-anim-in');
  });

  // Wait for all animations to finish before releasing _cartUpdating
  await new Promise(r => setTimeout(r, targets.length * ITEM_STEP_MS + ITEM_DUR_MS));
}

// ─── In-place qty update (no image reload) ───────────────────────────────────
// Surgically updates qty numbers and the footer total without touching images,
// titles, or option text — prevents any flash on +/− clicks.

function updateCartInPlace(updatedCart, cartBody) {
  if (!cartBody || !updatedCart) return;
  const taxRate = 0.10;

  updatedCart.items.forEach((item, index) => {
    const lineIndex = index + 1;
    const row = cartBody.querySelector(`.cart-item[data-line-index="${lineIndex}"]`);
    if (!row) return;
    const qty = item.quantity;
    const max = window.variantInventory?.[item.variant_id] ?? 999;
    const controls = row.querySelector('.cart-item__qty-controls');
    if (controls) { controls.dataset.current = qty; controls.dataset.max = max; }
    const qtyDisplay = row.querySelector('.qty-display');
    if (qtyDisplay) qtyDisplay.textContent = qty;
  });

  const totalEl = cartBody.querySelector('.cart-total-amount');
  if (totalEl) {
    totalEl.textContent = Shopify.formatMoney(Math.round(updatedCart.total_price * (1 + 0.10)));
  }
  const titleEl = cartBody.querySelector('.cart-title');
  if (titleEl) titleEl.textContent = `Cart (${updatedCart.item_count})`;

  updateQtyButtonsState();
  updateCartCount(updatedCart.item_count);
}

// ─── Load cart (background refresh) ─────────────────────────────────────────
// Renders from cache immediately (no blank flash), fetches fresh in the
// background, and skips rendering if a newer change has already landed.

async function loadCartIntoModal() {
  const cartBody = document.querySelector('.cart-modal__body');
  if (!cartBody) return;

  // Render cache immediately so the modal never opens to a blank body
  if (window.cart) {
    renderCartData(window.cart, cartBody);
  }
  // (No skeleton — if there's no cache, the body stays blank until the fetch resolves)

  if (_cartFetchPromise) return _cartFetchPromise;

  const versionAtFetchStart = _cartVersion;

  _cartFetchPromise = (async () => {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('Failed to load cart');
      const cart = await res.json();

      // A committed change already rendered newer data — don't overwrite it
      if (_cartVersion !== versionAtFetchStart) return;

      window.cart = cart;
      updateCartCount(cart.item_count);

      const corrected = await fixCartQuantities(cart);
      if (corrected) {
        _cartFetchPromise = null;
        return loadCartIntoModal();
      }

      const freshBody = document.querySelector('.cart-modal__body');
      // Animate if opening for first time (no items yet), plain render otherwise
      if (isCartOpen() && !freshBody.querySelector('.cart-item, .cart-empty')) {
        await animatedRenderCart(freshBody, cart);
      } else {
        renderCartData(cart, freshBody);
      }
    } catch (err) {
      console.error('Error loading cart:', err);
    } finally {
      _cartFetchPromise = null;
    }
  })();

  return _cartFetchPromise;
}

// ─── Commit a cart change ─────────────────────────────────────────────────────
// Sends /cart/change.js, uses the response (full cart) to update the UI.
// • newQty > 0  → updateCartInPlace (no image flash, instant feel)
// • newQty === 0 → animatedRenderCart (item gone, remaining items fade in)
// Increments _cartVersion to prevent a concurrent loadCartIntoModal from
// overwriting the result with stale data.

async function commitCartChange(line, newQty, optimisticUpdate) {
  if (_cartUpdating) return;
  _cartUpdating = true;

  if (optimisticUpdate) optimisticUpdate();

  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line, quantity: newQty }),
    });
    if (!res.ok) throw new Error(`/cart/change.js ${res.status}`);

    const updatedCart = await res.json();
    _cartVersion++;
    window.cart = updatedCart;

    const freshBody = document.querySelector('.cart-modal__body');
    if (newQty === 0) {
      await animatedRenderCart(freshBody, updatedCart);
    } else {
      updateCartInPlace(updatedCart, freshBody);
    }
    updateCartCount(updatedCart.item_count);
  } catch (err) {
    console.error('Cart change failed:', err);
    window.cart = null;
    _cartFetchPromise = null;
    await loadCartIntoModal();
  } finally {
    _cartUpdating = false;
  }
}

// ─── Event wiring ────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  const cartModal = document.getElementById('cart-modal');
  const cartBody  = document.querySelector('.cart-modal__body');

  // Cart toggle buttons
  document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.dataset.cartToggle === 'open') {
        openCartModal();
        loadCartIntoModal();
      } else if (btn.dataset.cartToggle === 'close') {
        closeCartModal();
      }
    });
  });

  // Close on backdrop click or "Continue Shopping"
  if (cartModal) {
    cartModal.addEventListener('click', e => {
      const modalContent = cartModal.querySelector('.cart-modal__content');
      if (e.target.closest('.continue-shopping') || !modalContent.contains(e.target)) {
        closeCartModal();
      }
    });
  }

  if (cartBody) {
    // ── Remove / Qty +/− ────────────────────────────────────────────────────
    cartBody.addEventListener('click', async event => {
      const removeBtn = event.target.closest('.cart-item__remove');
      if (removeBtn) {
        const line = removeBtn.dataset.removeIndex;
        if (!line) return;
        const row = removeBtn.closest('.cart-item');
        await commitCartChange(Number(line), 0, () => row?.remove());
        return;
      }

      const decreaseBtn = event.target.closest('.qty-decrease');
      const increaseBtn = event.target.closest('.qty-increase');
      if (!decreaseBtn && !increaseBtn) return;

      const controls = event.target.closest('.cart-item__qty-controls');
      if (!controls) return;
      const line    = Number(controls.dataset.lineIndex);
      const max     = Number(controls.dataset.max) || 999;
      const current = Number(controls.dataset.current);
      if (isNaN(line) || isNaN(current)) return;

      let newQty = current;
      if (decreaseBtn && current >= 1) newQty--;
      if (increaseBtn && current < max) newQty++;
      if (newQty === current) return;

      const qtyDisplay = controls.querySelector('.qty-display');
      await commitCartChange(line, newQty, () => {
        if (qtyDisplay) qtyDisplay.textContent = newQty;
        controls.dataset.current = newQty;
      });
    });

    // ── Direct qty input ────────────────────────────────────────────────────
    cartBody.addEventListener('change', async event => {
      if (!event.target.classList.contains('qty-input')) return;
      const container = event.target.closest('.cart-item__qty-controls');
      if (!container) return;
      const line = container.dataset.lineIndex;
      if (!line) return;
      let newQty = Number(event.target.value);
      if (isNaN(newQty) || newQty < 1) { newQty = 1; event.target.value = newQty; }
      await commitCartChange(Number(line), newQty, null);
    });
  }

  // Pre-fetch on page load so the cache is warm when the user opens the cart
  loadCartIntoModal();

  // Nav overlay (desktop hover)
  const pageOverlay = document.getElementById('page-overlay');
  document.querySelectorAll('.has-children').forEach(item => {
    item.addEventListener('mouseenter', () => pageOverlay?.classList.add('active'));
    item.addEventListener('mouseleave', () => pageOverlay?.classList.remove('active'));
  });
  document.querySelectorAll('.submenu').forEach(submenu => {
    submenu.addEventListener('mouseenter', () => pageOverlay?.classList.add('active'));
    submenu.addEventListener('mouseleave', () => pageOverlay?.classList.remove('active'));
  });
});
